/**
 * VK ID sign-in, web redirect flow.
 *
 * We generate the PKCE pair and `state` ourselves (as the Android app does)
 * and keep them in sessionStorage: VK returns only `code`, `state` and
 * `device_id` to /auth/vk/callback, and the API exchanges the code with VK
 * using the verifier and the app secret. The browser never sees VK tokens.
 */

/** VK ID web app (id.vk.ru/business). Public, same for every environment. */
const VK_ID_WEB_APP_ID = 54806331;

export const VK_CALLBACK_PATH = '/auth/vk/callback';

const VK_PKCE_KEY = 'vk_id_pkce';

interface VkPkceSession {
  state: string;
  codeVerifier: string;
}

/** RFC 7636 unreserved charset; 64 chars sits inside the 43-128 range. */
function randomUrlSafe(length: number): string {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

export function vkRedirectUri(): string {
  return `${window.location.origin}${VK_CALLBACK_PATH}`;
}

/** Leave for VK ID. Resolves only if the SDK fails before navigating away. */
export async function startVkIdLogin(): Promise<void> {
  const session: VkPkceSession = {
    state: randomUrlSafe(32),
    codeVerifier: randomUrlSafe(64),
  };
  sessionStorage.setItem(VK_PKCE_KEY, JSON.stringify(session));

  // Loaded on click: the SDK is only needed by the few visitors who use it.
  const VKID = await import('@vkid/sdk');
  VKID.Config.init({
    app: VK_ID_WEB_APP_ID,
    redirectUrl: vkRedirectUri(),
    state: session.state,
    codeVerifier: session.codeVerifier,
    scope: 'email',
    responseMode: VKID.ConfigResponseMode.Redirect,
    mode: VKID.ConfigAuthMode.Redirect,
  });
  await VKID.Auth.login();
}

/**
 * Read and forget the PKCE session for the returning `state`. Null when the
 * tab has none or the state differs (a forged or stale callback link), so the
 * code is never exchanged on behalf of a round-trip this tab did not start.
 */
export function consumeVkPkceSession(returnedState: string | null): string | null {
  const raw = sessionStorage.getItem(VK_PKCE_KEY);
  sessionStorage.removeItem(VK_PKCE_KEY);
  if (!raw || !returnedState) {
    return null;
  }
  try {
    const session = JSON.parse(raw) as VkPkceSession;
    return session.state === returnedState ? session.codeVerifier : null;
  } catch {
    return null;
  }
}
